import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import {
  ArtifactV1SessionPartKind,
  SessionArtifactPayload,
} from "@/mds/components/sessions/session.types";
import { MDSSessionNotificationInfo } from "@/mds/components/sessions/session-notification/session-notification.types";

export type MDSSessionArtifact = {
  kind: ArtifactV1SessionPartKind;
  payload: SessionArtifactPayload;
};

export enum MDSSessionErrorKind {
  SessionEventError = "SESSION_EVENT_ERROR",
}

export interface MDSSessionError {
  type: MDSSessionErrorKind;
  message: string;
  canRetry: boolean;
}

// UI slice state
export interface MDSSessionUIState {
  activeArtifact: MDSSessionArtifact | null;
  isMinimized: boolean;
  countdownDurationMs: number;
  isCountdownActive: boolean;
  isCountdownPaused: boolean;
  isInNotificationState: boolean;
  notificationInfo: MDSSessionNotificationInfo | null;
  isInputFocused: boolean;
  isHovering: boolean;
  sessionError: MDSSessionError | null;
  isPreview: boolean;
  sessionPrevMode: SessionViewMode;
}

// UI actions
export enum MDSSessionUIAction {
  SetActiveArtifact = "SET_ACTIVE_ARTIFACT",
  SetExtensionFocusState = "SET_EXTENSION_FOCUS_STATE",
  SetExtensionHoverState = "SET_EXTENSION_HOVER_STATE",
  SetExtensionInputFocusState = "SET_EXTENSION_INPUT_FOCUS_STATE",
  MinimizeSession = "MINIMIZE_SESSION",
  ExpandSession = "EXPAND_SESSION",
  SetPreviewMode = "SET_PREVIEW_MODE",
  StartCountdown = "START_COUNTDOWN",
  StopCountdown = "STOP_COUNTDOWN",
  PauseCountdown = "PAUSE_COUNTDOWN",
  ResumeCountdown = "RESUME_COUNTDOWN",
  SetInputFocused = "SET_INPUT_FOCUSED",
  SetHovering = "SET_HOVERING",
  SetNotificationState = "SET_NOTIFICATION_STATE",
  SetSessionError = "SET_SESSION_ERROR",
  ClearSessionError = "CLEAR_SESSION_ERROR",
  SetSessionPrevMode = "SET_SESSION_PREV_MODE",
}

export interface MDSSessionViewArtifactAction {
  type: MDSSessionUIAction.SetActiveArtifact;
  payload: MDSSessionArtifact | null;
}

export interface MDSSessionSetExtensionFocusStateAction {
  type: MDSSessionUIAction.SetExtensionFocusState;
  payload: boolean;
}

export interface MDSSessionSetExtensionHoverStateAction {
  type: MDSSessionUIAction.SetExtensionHoverState;
  payload: boolean;
}

export interface MDSSessionSetExtensionInputFocusStateAction {
  type: MDSSessionUIAction.SetExtensionInputFocusState;
  payload: boolean;
}

export interface MDSSessionMinimizeAction {
  type: MDSSessionUIAction.MinimizeSession;
}

export interface MDSSessionExpandAction {
  type: MDSSessionUIAction.ExpandSession;
}

export interface MDSSessionSetPreviewModeAction {
  type: MDSSessionUIAction.SetPreviewMode;
  payload: boolean;
}

export interface MDSSessionStartCountdownAction {
  type: MDSSessionUIAction.StartCountdown;
  payload: {
    durationMs: number;
  };
}

export interface MDSSessionStopCountdownAction {
  type: MDSSessionUIAction.StopCountdown;
}

export interface MDSSessionPauseCountdownAction {
  type: MDSSessionUIAction.PauseCountdown;
}

export interface MDSSessionResumeCountdownAction {
  type: MDSSessionUIAction.ResumeCountdown;
}

export interface MDSSessionSetInputFocusedAction {
  type: MDSSessionUIAction.SetInputFocused;
  payload: boolean;
}

export interface MDSSessionSetHoveringAction {
  type: MDSSessionUIAction.SetHovering;
  payload: boolean;
}

export interface MDSSessionSetNotificationStateAction {
  type: MDSSessionUIAction.SetNotificationState;
  payload: {
    isInNotificationState: boolean;
    notificationInfo?: MDSSessionNotificationInfo;
  };
}

export interface MDSSessionSetSessionErrorAction {
  type: MDSSessionUIAction.SetSessionError;
  payload: MDSSessionError;
}

export interface MDSSessionClearSessionErrorAction {
  type: MDSSessionUIAction.ClearSessionError;
}

export interface MDSSessionSetPrevModeAction {
  type: MDSSessionUIAction.SetSessionPrevMode;
  payload: SessionViewMode;
}

export type MDSSessionUIActionTypes =
  | MDSSessionViewArtifactAction
  | MDSSessionSetExtensionFocusStateAction
  | MDSSessionSetExtensionHoverStateAction
  | MDSSessionSetExtensionInputFocusStateAction
  | MDSSessionMinimizeAction
  | MDSSessionExpandAction
  | MDSSessionSetPreviewModeAction
  | MDSSessionStartCountdownAction
  | MDSSessionStopCountdownAction
  | MDSSessionPauseCountdownAction
  | MDSSessionResumeCountdownAction
  | MDSSessionSetInputFocusedAction
  | MDSSessionSetHoveringAction
  | MDSSessionSetNotificationStateAction
  | MDSSessionSetSessionErrorAction
  | MDSSessionClearSessionErrorAction
  | MDSSessionSetPrevModeAction;
